import { useState } from 'react';
import PlateFromSlantForm from '../../PlateFromSlantForm';
import SlantStoragePanel from '../../SlantStoragePanel';
import { defaultPlateId, defaultSlantId, liveSlant, reviewBody, reviveSlantId } from '../quickActions';
import { FormButtons, FormError, PlatePicker, SlantPicker, stack, useQuickSubmit } from './shared';
import * as S from '../../specimens/styles';

// slant_status (0000): what a review may find a slant to be.
const SLANT_STATUSES = ['healthy', 'contaminated', 'in_refresh', 'discarded'];

/*
 * Cut the bank's slant set from a plate (POST /slants {plate_id}): the
 * four-slant set, active/active/refresh/backup (slants.py:129).
 */
export function CutSlantsForm({ item, plates, extra = {}, t, onDone, onCancel }) {
  const [plateId, setPlateId] = useState(extra.plateId || defaultPlateId(item, plates));
  const { busy, error, send } = useQuickSubmit('hub-slants');
  const submit = async (e) => {
    e.preventDefault();
    const res = await send('post', '/slants', { plate_id: plateId });
    if (!res) return;
    const codes = (res.data?.slants || []).map((s) => s.slant_code).filter(Boolean).join(', ');
    onDone(codes ? `${t('toast.slantsCut')} (${codes})` : t('toast.slantsCut'));
  };
  if (!plates.length) return <p style={S.hint}>{t('quick.noPlates')}</p>;
  return (
    <form onSubmit={submit} style={stack} data-testid="quick-cut-slants">
      <PlatePicker plates={plates} value={plateId} onChange={setPlateId} t={t} />
      <p style={S.hint}>{t('quick.slants.cutNote')}</p>
      <FormError>{error}</FormError>
      <FormButtons busy={busy} onCancel={onCancel} t={t} saveLabel={t('quick.slants.cut')} />
    </form>
  );
}

/*
 * Where a slant is kept (SlantStoragePanel, the Bank page's own). It is fed
 * the dossier's slant -- with who banked it (`preserved_by`) and its taxon --
 * because the panel fills an empty "preserved by" with the person at the
 * bench: given a slant without it, saving would rewrite who banked it. The
 * panel sends only the keys that changed.
 */
export function SetStorageForm({ item, slants, extra = {}, users, user, t, onDone }) {
  const [slantId, setSlantId] = useState(extra.slantId || defaultSlantId(slants, { unbankedFirst: true }));
  const found = slants.find((s) => s.slant_id === slantId);
  // The tube's Isolate ID and organism default to the isolate's own where
  // the dossier's slant does not carry them.
  const slant = found && { ...found, specimen_code: found.specimen_code || item?.specimen_code,
    taxon_name: found.taxon_name || item?.taxon_name };
  if (!slants.length) return <p style={S.hint}>{t('quick.noSlants')}</p>;
  return (
    <div style={stack} data-testid="quick-set-storage">
      <SlantPicker slants={slants} value={slantId} onChange={setSlantId} t={t} />
      {slant && (
        <SlantStoragePanel key={slant.slant_id} slant={slant} canEdit users={users} currentUser={user}
          onSaved={() => onDone(t('toast.storageSaved', { code: slant.slant_code }))} />
      )}
    </div>
  );
}

/* Log a review of one slant (POST /slants/{id}/reviews): what it was found
 * to be and what was done. A review clears "review due". */
export function ReviewForm({ slants, extra = {}, t, onDone, onCancel }) {
  const [slantId, setSlantId] = useState(extra.slantId || defaultSlantId(slants));
  const slant = slants.find((s) => s.slant_id === slantId);
  const [form, setForm] = useState({ status_at_review: slant?.status || 'healthy', action_taken: '', notes: '' });
  const { busy, error, send } = useQuickSubmit('hub-review');
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const res = await send('post', `/slants/${slantId}/reviews`, reviewBody(form));
    if (res) onDone(t('toast.reviewLogged', { code: slant?.slant_code || '' }));
  };
  if (!slants.length) return <p style={S.hint}>{t('quick.noSlants')}</p>;
  return (
    <form onSubmit={submit} style={stack} data-testid="quick-review">
      <SlantPicker slants={slants} value={slantId} onChange={setSlantId} t={t} />
      <label style={S.label}>
        {t('quick.review.status')}
        <select value={form.status_at_review} onChange={set('status_at_review')} style={S.input}
          title={t('tip.quick.reviewStatus')}>
          {SLANT_STATUSES.map((s) => <option key={s} value={s}>{t(`slants:slantStatus.${s}`)}</option>)}
        </select>
      </label>
      <label style={S.label}>
        {t('quick.review.action')}
        <input value={form.action_taken} onChange={set('action_taken')} maxLength={128} style={S.input}
          title={t('tip.quick.reviewAction')} />
      </label>
      <label style={S.label}>
        {t('quick.notes')}
        <textarea rows={2} value={form.notes} onChange={set('notes')} style={S.input} title={t('tip.quick.notes')} />
      </label>
      <FormError>{error}</FormError>
      <FormButtons busy={busy} onCancel={onCancel} t={t} saveLabel={t('quick.review.save')} />
    </form>
  );
}

/** Where a tube is kept, short: "Freezer B / 3 / 12 / C7", the unit's own
 *  name when no Freezer ID names it; "Not in storage" for a loose tube. */
function tubePlace(s, t) {
  if (!s.storage_unit) return t('slants:storage.notStored');
  return s.storage_location || [s.storage_unit_label || t(`specimens:storage.units.${s.storage_unit}`),
    s.storage_rack, s.storage_box, s.storage_well].filter(Boolean).join(' / ');
}

/*
 * Revive from bank: grow one new plate from a tube (PlateFromSlantForm, the
 * Lab Work page's own), opened on the chosen tube and its medium. The tube
 * stays in the bank; the plate joins the Lab lane.
 *
 * It opens on the tube the bank keeps to be grown from (reviveSlantId: a
 * banked, healthy, active one first), and the picker lists the live tubes
 * with where each is kept, so the reader knows which one to take out. A
 * discarded or archived tube is never offered, even when the form was
 * opened on it: nothing is grown from it (409 slant_not_usable).
 */
export function ReviveForm({ slants, extra = {}, protocols, t, onDone, onCancel }) {
  const choices = slants.filter(liveSlant);
  const [slantId, setSlantId] = useState(() => (choices.some((s) => s.slant_id === extra.slantId)
    ? extra.slantId : reviveSlantId(slants)));
  const slant = choices.find((s) => s.slant_id === slantId);
  if (!slants.length) return <p style={S.hint}>{t('quick.noSlants')}</p>;
  if (!choices.length) return <p style={S.hint} data-testid="quick-revive-none">{t('quick.revive.noLiveTube')}</p>;
  return (
    <div style={stack} data-testid="quick-revive">
      <SlantPicker slants={choices} value={slantId} onChange={setSlantId} t={t}
        describe={(s) => [s.slant_code, tubePlace(s, t), t(`slants:slantStatus.${s.status}`)].join(' · ')} />
      <p style={{ ...S.hint, margin: 0 }} data-testid="quick-revive-explain">{t('quick.revive.explain')}</p>
      {slant && (
        <PlateFromSlantForm key={slant.slant_id} protocols={protocols} onCancel={onCancel}
          initialSlant={{ slant_id: slant.slant_id, slant_code: slant.slant_code, protocol: slant.protocol || '' }}
          onCreated={(plate) => onDone(t('toast.revived', { code: plate?.plate_code || '' }))} />
      )}
    </div>
  );
}
